import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar2Week,
  CalendarPlus,
  ChevronRight,
  Clock,
  XCircle,
} from "react-bootstrap-icons";

import PageHeader from "../../components/PageHeader";
import LoadingSpinner from "../../components/LoadingSpinner";
import { ApiConnectionString } from "../../constants/ApiConntection";
import { useAuthContext } from "../../hooks/useAuthContext";

import "./AppointmentsPage.css";

export default function AppointmentsPage({ profile }) {
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user?.token) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setErr("");

      const response = await axios.get(
        `${ApiConnectionString}/api/client/appointments`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.appointments ||
          response.data?.Appointments ||
          [];

      setAppointments(data);
    } catch (error) {
      console.error(error);
      setErr("Your appointments could not be loaded.");
    } finally {
      setIsLoading(false);
    }
  };

  const getAppointmentId = (appointment) => {
    return appointment?.id || appointment?.Id;
  };

  const getAppointmentStart = (appointment) => {
    return appointment?.start || appointment?.Start || "";
  };

  const getAppointmentFinish = (appointment) => {
    return appointment?.finish || appointment?.Finish || "";
  };

  const getAppointmentTitle = (appointment) => {
    return (
      appointment?.name ||
      appointment?.Name ||
      appointment?.title ||
      appointment?.Title ||
      "Therapy session"
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return "Date unavailable";
    }

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return "Date unavailable";
    }

    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) {
      return "";
    }

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isPastAppointment = (appointment) => {
    const finish = getAppointmentFinish(appointment);
    const start = getAppointmentStart(appointment);
    const comparisonDate = new Date(finish || start);

    if (Number.isNaN(comparisonDate.getTime())) {
      return false;
    }

    return comparisonDate.getTime() < Date.now();
  };

  const upcomingAppointments = appointments
    .filter((appointment) => !isPastAppointment(appointment))
    .sort((first, second) => {
      return (
        new Date(getAppointmentStart(first)).getTime() -
        new Date(getAppointmentStart(second)).getTime()
      );
    });

  const pastAppointments = appointments
    .filter((appointment) => isPastAppointment(appointment))
    .sort((first, second) => {
      return (
        new Date(getAppointmentStart(second)).getTime() -
        new Date(getAppointmentStart(first)).getTime()
      );
    });

  const renderAppointment = (
    appointment,
    { isPast = false, isNext = false } = {}
  ) => {
    const id = getAppointmentId(appointment);
    const start = getAppointmentStart(appointment);
    const finish = getAppointmentFinish(appointment);

    return (
      <button
        type="button"
        className={`client-appointment-row ${
          isPast ? "client-appointment-row-past" : ""
        } ${isNext ? "client-appointment-row-next" : ""}`}
        key={id || start}
        onClick={() => navigate(`/appointments/${id}`)}
      >
        <div className="client-appointment-date-icon">
          <Calendar2Week />
        </div>

        <div className="client-appointment-main">
          {isNext && (
            <span className="client-next-label">Next appointment</span>
          )}

          <strong>{formatDate(start)}</strong>

          <span className="client-appointment-time">
            <Clock />

            {formatTime(start)}

            {finish && ` – ${formatTime(finish)}`}
          </span>

          <small>{getAppointmentTitle(appointment)}</small>
        </div>

        <div className="client-appointment-right">
          <ChevronRight />
        </div>
      </button>
    );
  };

  if (isLoading) {
    return (
      <div className="client-appointments-loading">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="client-appointments-page">
      <PageHeader
        title="Appointments"
        text="View your upcoming and previous therapy appointments."
      />

      {profile?.canSelfBookAppointments && (
        <button
          type="button"
          className="client-book-appointment-link"
          onClick={() => navigate("/bookAppointment")}
        >
          <CalendarPlus />
          <span>Book an appointment</span>
          <ChevronRight />
        </button>
      )}

      {err ? (
        <div className="client-appointments-error-card">
          <div className="client-appointments-error">
            <XCircle />
            <span>{err}</span>
          </div>

          <button
            type="button"
            className="secondary-btn"
            onClick={fetchData}
          >
            Try again
          </button>
        </div>
      ) : appointments.length === 0 ? (
        <div className="client-appointments-empty">
          <div className="client-appointments-empty-icon">
            <Calendar2Week />
          </div>

          <h2>No appointments yet</h2>

          <p>
            {profile?.canSelfBookAppointments
              ? "You do not currently have any appointments booked."
              : "Your appointments will appear here once they are booked."}
          </p>

          {profile?.canSelfBookAppointments && (
            <button
              type="button"
              className="secondary-btn"
              onClick={() => navigate("/bookAppointment")}
            >
              Book an appointment
            </button>
          )}
        </div>
      ) : (
        <div className="client-appointments-sections">
          <section className="client-appointments-section">
            <div className="client-appointments-section-header">
              <div>
                <h2>Upcoming</h2>
                <p>Your scheduled appointments.</p>
              </div>
            </div>

            {upcomingAppointments.length > 0 ? (
              <div className="client-appointments-list">
                {upcomingAppointments.map((appointment, index) =>
                  renderAppointment(appointment, {
                    isNext: index === 0,
                  })
                )}
              </div>
            ) : (
              <div className="client-appointments-section-empty">
                <Calendar2Week />

                <div>
                  <strong>No upcoming appointments</strong>

                  <p>
                    You do not currently have another appointment booked.
                  </p>
                </div>

                {profile?.canSelfBookAppointments && (
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => navigate("/bookAppointment")}
                  >
                    Book an appointment
                  </button>
                )}
              </div>
            )}
          </section>

          {pastAppointments.length > 0 && (
            <section className="client-appointments-section client-past-section">
              <div className="client-appointments-section-header">
                <div>
                  <h2>Past appointments</h2>
                  <p>Your previous sessions.</p>
                </div>
              </div>

              <div className="client-appointments-list">
                {pastAppointments.map((appointment) =>
                  renderAppointment(appointment, {
                    isPast: true,
                  })
                )}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}